import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import axios from "axios";

const API = process.env.REACT_APP_API_URL || "";

const ProductListingPage = () => {
    const [products, setProducts] = useState([]);
    const [categories, setCategories] = useState([]);
    const [loading, setLoading] = useState(true);
    const [activeCategory, setActiveCategory] = useState("all");

    useEffect(() => {
        const fetchData = async () => {
            try {
                const [prodRes, catRes] = await Promise.all([
                    axios.get(`${API}/api/store/products`),
                    axios.get(`${API}/api/store/categories`)
                ]);
                setProducts(prodRes.data);
                setCategories(catRes.data);
            } catch (err) {
                console.error(err);
            } finally {
                setLoading(false);
            }
        };
        fetchData();
    }, []);

    const filteredProducts = activeCategory === "all" 
        ? products 
        : products.filter(p => p.category?.slug === activeCategory);

    return (
        <div className="min-h-screen bg-neutral-950 text-white font-sans pt-32 pb-24 px-6 md:px-12">
            <div className="max-w-7xl mx-auto">
                {/* Header */}
                <div className="mb-12 border-b border-neutral-800 pb-8">
                    <h1 className="text-4xl md:text-5xl font-bold tracking-tight mb-4">All Gear</h1>
                    <p className="text-neutral-400">Professional-grade hardware for your missions.</p>
                </div>

                <div className="flex flex-col md:flex-row gap-12">
                    {/* Sidebar Filters */}
                    <div className="w-full md:w-64 shrink-0">
                        <div className="sticky top-32">
                            <h3 className="text-lg font-bold mb-6 text-neutral-300">Categories</h3>
                            <ul className="space-y-3">
                                <li>
                                    <button 
                                        onClick={() => setActiveCategory("all")}
                                        className={`text-sm transition-colors ${activeCategory === "all" ? "text-purple-400 font-bold" : "text-neutral-500 hover:text-white"}`}
                                    >
                                        All Products
                                    </button>
                                </li>
                                {categories.map(cat => (
                                    <li key={cat._id}>
                                        <button 
                                            onClick={() => setActiveCategory(cat.slug)}
                                            className={`text-sm transition-colors ${activeCategory === cat.slug ? "text-purple-400 font-bold" : "text-neutral-500 hover:text-white"}`}
                                        >
                                            {cat.name}
                                        </button>
                                    </li>
                                ))}
                            </ul>
                        </div>
                    </div>

                    {/* Product Grid */}
                    <div className="flex-1">
                        {loading ? (
                            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                                {[1,2,3,4,5,6].map(i => (
                                    <div key={i} className="h-80 bg-neutral-900/50 rounded-2xl animate-pulse" />
                                ))}
                            </div>
                        ) : filteredProducts.length === 0 ? (
                            <div className="text-center py-20 bg-neutral-900/20 rounded-3xl border border-neutral-800 border-dashed">
                                <p className="text-neutral-400">No products found in this category.</p>
                            </div>
                        ) : (
                            <motion.div 
                                layout
                                className="grid grid-cols-1 sm:grid-cols-2 xl:grid-cols-3 gap-6"
                            >
                                {filteredProducts.map(product => (
                                    <motion.div 
                                        layout
                                        initial={{ opacity: 0, scale: 0.9 }}
                                        animate={{ opacity: 1, scale: 1 }}
                                        exit={{ opacity: 0, scale: 0.9 }}
                                        transition={{ duration: 0.3 }}
                                        key={product._id} 
                                        className="group"
                                    >
                                        <Link to={`/store/products/${product.slug}`} className="block bg-neutral-900/30 border border-neutral-800 rounded-2xl overflow-hidden hover:border-purple-500/50 hover:bg-neutral-900/60 transition-all duration-300">
                                            <div className="aspect-square relative overflow-hidden bg-neutral-950 p-6 flex items-center justify-center">
                                                {product.images?.[0] ? (
                                                    <img 
                                                        src={product.images[0].imageUrl} 
                                                        alt={product.name}
                                                        className="max-w-full max-h-full object-contain group-hover:scale-105 transition-transform duration-500"
                                                    />
                                                ) : (
                                                    <span className="text-neutral-700">No Image</span>
                                                )}
                                                {product.status === 'out_of_stock' && (
                                                    <div className="absolute top-3 left-3 bg-red-500 text-white text-[10px] font-bold px-2 py-1 rounded">
                                                        SOLD OUT
                                                    </div>
                                                )}
                                            </div>
                                            <div className="p-5 border-t border-neutral-800">
                                                <h3 className="font-semibold text-lg mb-1 truncate">{product.name}</h3>
                                                <div className="flex justify-between items-end">
                                                    <div className="font-mono">
                                                        {product.discountPrice ? (
                                                            <div className="flex flex-col">
                                                                <span className="text-xs text-neutral-500 line-through">₹{product.basePrice}</span>
                                                                <span className="text-white font-bold">₹{product.discountPrice}</span>
                                                            </div>
                                                        ) : (
                                                            <span className="text-white font-bold">₹{product.basePrice}</span>
                                                        )}
                                                    </div>
                                                </div>
                                            </div>
                                        </Link>
                                    </motion.div>
                                ))}
                            </motion.div>
                        )}
                    </div>
                </div>
            </div>
        </div>
    );
};

export default ProductListingPage;
